<template>
  <div class="treatment-plans">
    <div class="treatment-plans-container">
      <!-- Patient Selection -->
      <div v-if="family.length > 1" class="patient-selection-section">
        <div class="selection-card">
          <h3 class="selection-title">View Plan For</h3>
          <v-select
            v-model="selectedPatient"
            :items="family"
            :item-text="item => `${item.firstName} ${item.lastName}`" 
            item-value="item => item.id"
            label="Select Patient"
            return-object
            class="patient-selector"
            outlined
            dense
            @change="handlePatientSelect" />
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
          class="loading-spinner" />
      </div>

      <!-- Treatment Plans Content -->
      <div v-if="!loading" class="treatment-plans-content">
        <!-- Treatment Plan Cards -->
        <div v-if="plannedAppointments.length" class="treatment-plan-list">
          <div
            v-for="(plan, i) in plannedAppointments"
            :key="i"
            class="treatment-plan-card">
            
            <!-- Plan Header -->
            <div class="plan-header">
              <div class="plan-title-section">
                <h3 class="plan-title">{{ plan.description }}</h3>
                <p v-if="plan.note" class="plan-note">{{ plan.note }}</p>
              </div>
              <v-btn 
                @click="navigateToBookAppointment(plan)" 
                class="btn-primary book-btn">
                <v-icon left size="18">mdi-calendar-plus</v-icon>
                Book Appointment
              </v-btn>
            </div>

            <!-- Plan Details -->
            <div class="plan-details">
              <div class="detail-grid">
                <div class="detail-item">
                  <h4 class="detail-label">Provider</h4>
                  <div class="detail-value provider-info">
                    <div class="provider-avatar">
                      {{ getProviderInitials(plan.items[0]) }}
                    </div>
                    <span class="provider-name">{{ plan.items[0].providerFirstName }} {{ plan.items[0].providerLastName }}</span>
                  </div>
                </div>

                <div class="detail-item">
                  <h4 class="detail-label">Duration</h4>
                  <div class="detail-value duration-info">
                    <v-icon size="16" class="duration-icon">mdi-clock-outline</v-icon>
                    <span>{{ formatDuration(determineProcedureLength(plan.items, plan.pattern)) }}</span>
                  </div>
                </div>

                <div class="detail-item cost-estimate">
                  <h4 class="detail-label">Cost Estimate</h4>
                  <div v-if="plan.fee > 0" class="detail-value cost-info">
                    <div class="cost-row">
                      <span class="cost-label">Overall Fee:</span>
                      <span class="cost-amount overall-fee">{{ formatCurrency(plan.fee) }}</span>
                    </div>
                    <div class="cost-row">
                      <span class="cost-label">Patient Portion:</span>
                      <span class="cost-amount patient-portion">{{ formatCurrency(plan.patientPortion) }}</span>
                    </div>
                  </div>
                  <div v-else class="detail-value no-estimate">
                    <v-icon size="16" class="warning-icon">mdi-information-outline</v-icon>
                    <span>Unable to estimate fee for this procedure</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Procedures Table -->
            <div class="procedures-section">
              <h4 class="procedures-title">Procedure Details</h4>
              <div class="procedures-table">
                <div class="table-header">
                  <div class="header-cell code-col">Code</div>
                  <div class="header-cell tooth-col">Tooth</div>
                  <div class="header-cell description-col">Description</div>
                  <div class="header-cell fee-col">Fee</div>
                  <div class="header-cell allowed-col">Allowed</div>
                  <div class="header-cell insurance-col">Insurance</div>
                  <div class="header-cell patient-col">Patient</div>
                </div>
                
                <div class="table-body">
                  <div 
                    v-for="(item, idx) in plan.items" 
                    :key="idx"
                    class="table-row">
                    <div class="table-cell code-col">
                      <span class="procedure-code">{{ item.procedureCode }}</span>
                    </div>
                    <div class="table-cell tooth-col">
                      <span class="tooth-number">{{ item.toothNumber || '-' }}</span>
                    </div>
                    <div class="table-cell description-col">
                      <span class="procedure-description">{{ item.procedureCodeDescription }}</span>
                    </div>
                    <div class="table-cell fee-col">
                      <span class="amount">{{ item.fee > 0 ? formatCurrency(item.fee) : '-' }}</span>
                    </div>
                    <div class="table-cell allowed-col">
                      <span class="amount">{{ item.fee > 0 ? formatCurrency(item.insuranceAllowedAmount) : '-' }}</span>
                    </div>
                    <div class="table-cell insurance-col">
                      <span class="amount">{{ item.fee > 0 ? formatCurrency(item.primaryInsuranceAmount) : '-' }}</span>
                    </div>
                    <div class="table-cell patient-col">
                      <span class="amount patient-amount">{{ item.fee > 0 ? formatCurrency(item.patientPortion) : '-' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <div class="empty-state-icon">
            <v-icon size="64" color="#9ca3af">mdi-clipboard-text-outline</v-icon>
          </div>
          <h3 class="empty-state-title">No Treatment Plans</h3>
          <p class="empty-state-message">
            There are no current treatment plans for this patient. Treatment plans will appear here once they are created by your dental provider.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataService from '../../_services/DataService';
import Utils from '../utils';

export default {
  name: 'PatientTreatmentPlans',
  data() {
    return {
      pageName: 'Treatment Plan',
      page: 1,
      loading: true,
      selectedPatient: {},
      family: [],
      treatmentPlans: [],
      plannedAppointments: [],
      timeIncrementPreference: 10,
      formatCurrency: Utils.formatCurrency,
      formatDuration: Utils.formatDuration,
    };
  },
  methods: {
    getProviderInitials(item) {
      const first = item.providerFirstName ? item.providerFirstName.charAt(0) : '';
      const last = item.providerLastName ? item.providerLastName.charAt(0) : '';
      return first + last;
    },
    async getPatientFamily() {
      const response = await DataService.getPatientFamily();
      if (response.status === 200) {
        this.selectedPatient = response.data.find((patient) => patient.status === 'Self');
        const rest = response.data.filter((patient) => patient.status !== 'Self');
        rest.sort((a, b) => a.firstName > b.firstName ? 1 : -1); // first name sort because family. diff last names are shrug
        this.family = [this.selectedPatient, ...rest];
      }
    },
    async getPatientTreatmentPlan() {
      const response = await DataService.getTreatmentPlansForPatient(this.selectedPatient.status === 'Self' ? undefined : this.selectedPatient.id);
      if (response.status === 200) {
        this.treatmentPlans = response.data.treatmentPlans;
        this.plannedAppointments = response.data.plannedAppointments;
      }
    },
    async handlePatientSelect(item) {
      this.loading = true;

      this.selectedPatient = item;
      await this.getPatientTreatmentPlan();

      this.loading = false;
    },
    getProcedureLengthDisplay(items) {
      let minutes = this.determineProcedureLength(items);
      const hours = Math.floor(minutes / 60);
      if (hours > 0) {
        minutes = minutes % 60;
        return `${hours}hr ${minutes}min`;
      }

      return `${minutes} min`;
    },
    determineProcedureLength(items, pattern) {
      if (pattern) {
        return pattern.length * 5;
      }

      let xCount = 0;
      let isAllHygiene = true;
      for (const item of items) {
        if (item.isHygiene) {
          xCount += item.procedureCodePattern.length;
        } else {
          isAllHygiene = false;
          for (const char of item.procedureCodePattern) {
            if (char.toLowerCase() === 'x') {
              xCount++;
            }
          }
        }
      }

      // For normal appointments, provider time bracketed is by
      // hygienist time (two slashes), times five minutes per item
      if (!isAllHygiene) {
        xCount += 2;
      }

      return xCount * 5;
    },
    navigateToBookAppointment(plan) {
      this.$router.push({
        name: 'PatientSchedule',
        query: {
          patientId: this.selectedPatient.id,
          providerId: plan.items[0].providerId,
          pattern: plan.pattern,
          plannedAppointmentId: plan.plannedAppointmentId,
          duration: this.determineProcedureLength(plan.items, plan.pattern),
          description: `Treatment of ${plan.description} plan`,
        },
      });
    },
  },
  async mounted() {
    this.loading = true;

    await this.getPatientFamily();
    await this.getPatientTreatmentPlan();

    this.loading = false;
  },
};
</script>

<style lang="scss">
@import '@/scss/variables.scss';
@import '@/scss/mixins/_mobile-nav-spacing.scss';

// Component-specific color variables
$treatment-plan-bg-color: white;
$treatment-plan-border-color: #e2e8f0;
$treatment-plan-hover-color: #f8fafc;
$page-title-color: #1f2937;
$selection-card-bg-color: white;
$selection-title-color: #374151;
$plan-title-color: #1f2937;
$plan-note-color: #6b7280;
$detail-label-color: #374151;
$detail-value-color: #6b7280;
$provider-avatar-bg-color: #133b54;
$provider-avatar-text-color: white;
$provider-name-color: #374151;
$duration-icon-color: #9ca3af;
$cost-label-color: #6b7280;
$overall-fee-color: #374151;
$patient-portion-color: #f59e0b;
$warning-icon-color: #f59e0b;
$no-estimate-color: #6b7280;
$procedures-title-color: #374151;
$table-header-bg-color: #f8fafc;
$table-header-text-color: #374151;
$table-row-border-color: #f1f5f9;
$table-row-hover-color: #f8fafc;
$procedure-code-color: #1f2937;
$tooth-number-color: #6b7280;
$procedure-description-color: #374151;
$amount-color: #374151;
$patient-amount-color: #f59e0b;
$empty-state-bg-color: #f9fafb;
$empty-state-border-color: #e5e7eb;
$empty-state-title-color: #374151;
$empty-state-text-color: #6b7280;

.treatment-plans {
  @include mobile-nav-spacing;
  
  .treatment-plans-container {
    padding: 16px;
  }
  
  .patient-selection-section {
    margin-bottom: 32px;
    
    @media (max-width: $breakpoint-mobile) {
      margin-bottom: 24px;
    }
    
    .selection-card {
      background: $selection-card-bg-color;
      border: 1px solid $treatment-plan-border-color;
      border-radius: 12px;
      padding: 24px;
      box-shadow: $shadow-sm;
      
      @media (max-width: $breakpoint-mobile) {
        padding: 20px;
      }
      
      .selection-title {
        font-size: 18px;
        font-weight: 600;
        color: $selection-title-color;
        margin: 0 0 16px 0;
        
        @media (max-width: $breakpoint-mobile) {
          font-size: 16px;
        }
      }
      
      .patient-selector {
        max-width: 400px;
      }
    }
  }
  
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    
    .loading-spinner {
      display: block;
    }
  }
  
  .treatment-plans-content {
    .treatment-plan-list {
      display: flex;
      flex-direction: column;
      gap: 24px;
      
      @media (max-width: $breakpoint-mobile) {
        gap: 20px;
      }
    }
    
    .treatment-plan-card {
      background: $treatment-plan-bg-color;
      border: 1px solid $treatment-plan-border-color;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: $shadow-md;
      
      .plan-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 24px 24px 20px 24px;
        border-bottom: 1px solid $treatment-plan-border-color;
        
        @media (max-width: $breakpoint-mobile) {
          flex-direction: column;
          gap: 16px;
          padding: 20px 20px 16px 20px;
        }
        
        .plan-title-section {
          flex: 1;
          
          .plan-title {
            font-size: 20px;
            font-weight: 600;
            color: $plan-title-color;
            margin: 0 0 8px 0;
            line-height: 1.3;
            
            @media (max-width: $breakpoint-mobile) {
              font-size: 18px;
            }
          }
          
          .plan-note {
            color: $plan-note-color;
            font-size: 14px;
            margin: 0;
            font-style: italic;
            
            @media (max-width: $breakpoint-mobile) {
              font-size: 13px;
            }
          }
        }
        
        .book-btn {
          flex-shrink: 0;
          
          @media (max-width: $breakpoint-mobile) {
            width: 100%;
          }
        }
      }
      
      .plan-details {
        padding: 24px;
        
        @media (max-width: $breakpoint-mobile) {
          padding: 20px;
        }
        
        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 24px;
          
          @media (max-width: $breakpoint-tablet) {
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            
            .cost-estimate {
              grid-column: 1 / -1;
            }
          }
          
          @media (max-width: $breakpoint-mobile) {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }
        
        .detail-item {
          .detail-label {
            font-size: 14px;
            font-weight: 600;
            color: $detail-label-color;
            margin: 0 0 8px 0;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .detail-value {
            color: $detail-value-color;
            
            &.provider-info {
              display: flex;
              align-items: center;
              gap: 12px;
              
              .provider-avatar {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: $provider-avatar-bg-color;
                color: $provider-avatar-text-color;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 600;
                font-size: 12px;
                flex-shrink: 0;
              }
              
              .provider-name {
                font-size: 15px;
                font-weight: 500;
                color: $provider-name-color;
              }
            }
            
            &.duration-info {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 15px;
              font-weight: 500;
              
              .duration-icon {
                color: $duration-icon-color;
              }
            }
            
            &.cost-info {
              @media (max-width: $breakpoint-tablet) {
                text-align: left;
              }
              
              .cost-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 8px;
                
                @media (max-width: $breakpoint-tablet) {
                  justify-content: flex-start;
                  gap: 12px;
                }
                
                &:last-child {
                  margin-bottom: 0;
                }
                
                .cost-label {
                  font-size: 14px;
                  color: $cost-label-color;
                  
                  @media (max-width: $breakpoint-tablet) {
                    min-width: 100px;
                  }
                }
                
                .cost-amount {
                  font-weight: 600;
                  font-size: 15px;
                  
                  &.overall-fee {
                    color: $overall-fee-color;
                  }
                  
                  &.patient-portion {
                    color: $patient-portion-color;
                  }
                }
              }
            }
            
            &.no-estimate {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 14px;
              color: $no-estimate-color;
              
              .warning-icon {
                color: $warning-icon-color;
              }
            }
          }
        }
      }
      
      .procedures-section {
        border-top: 1px solid $treatment-plan-border-color;
        
        .procedures-title {
          font-size: 16px;
          font-weight: 600;
          color: $procedures-title-color;
          margin: 0;
          padding: 20px 24px 16px 24px;
          
          @media (max-width: $breakpoint-mobile) {
            padding: 16px 20px 12px 20px;
            font-size: 14px;
          }
        }
        
        .procedures-table {
          .table-header {
            display: grid;
            grid-template-columns: 80px 60px 2fr 100px 100px 100px 100px;
            background: $table-header-bg-color;
            border-top: 1px solid $treatment-plan-border-color;
            
            @media (max-width: $breakpoint-tablet) {
              grid-template-columns: 70px 50px 1.5fr 80px 80px 80px 80px;
            }
            
            @media (max-width: $breakpoint-mobile) {
              display: none;
            }
            
            .header-cell {
              padding: 12px 16px;
              font-size: 12px;
              font-weight: 600;
              color: $table-header-text-color;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              
              @media (max-width: $breakpoint-tablet) {
                padding: 10px 12px;
                font-size: 11px;
              }
              
              &.fee-col,
              &.allowed-col,
              &.insurance-col,
              &.patient-col {
                text-align: right;
              }
            }
          }
          
          .table-body {
            .table-row {
              display: grid;
              grid-template-columns: 80px 60px 2fr 100px 100px 100px 100px;
              border-bottom: 1px solid $table-row-border-color;
              transition: background-color $transition-fast ease;
              
              @media (max-width: $breakpoint-tablet) {
                grid-template-columns: 70px 50px 1.5fr 80px 80px 80px 80px;
              }
              
              @media (max-width: $breakpoint-mobile) {
                grid-template-columns: 1fr;
                gap: 8px;
                padding: 16px 20px;
                border-bottom: 2px solid $table-row-border-color;
              }
              
              &:hover {
                background: $table-row-hover-color;
              }
              
              &:last-child {
                border-bottom: none;
              }
              
              .table-cell {
                padding: 16px;
                display: flex;
                align-items: center;
                
                @media (max-width: $breakpoint-tablet) {
                  padding: 12px;
                }
                
                @media (max-width: $breakpoint-mobile) {
                  padding: 0;
                  
                  &:before {
                    content: attr(data-label);
                    font-weight: 600;
                    color: $table-header-text-color;
                    margin-right: 8px;
                    min-width: 80px;
                  }
                }
                
                &.code-col {
                  .procedure-code {
                    font-weight: 600;
                    color: $procedure-code-color;
                    font-size: 14px;
                  }
                  
                  @media (max-width: $breakpoint-mobile) {
                    &:before {
                      content: 'Code: ';
                    }
                  }
                }
                
                &.tooth-col {
                  .tooth-number {
                    color: $tooth-number-color;
                    font-size: 14px;
                    font-weight: 500;
                  }
                  
                  @media (max-width: $breakpoint-mobile) {
                    &:before {
                      content: 'Tooth: ';
                    }
                  }
                }
                
                &.description-col {
                  .procedure-description {
                    color: $procedure-description-color;
                    font-size: 14px;
                    line-height: 1.4;
                  }
                  
                  @media (max-width: $breakpoint-mobile) {
                    &:before {
                      content: 'Description: ';
                    }
                  }
                }
                
                &.fee-col,
                &.allowed-col,
                &.insurance-col,
                &.patient-col {
                  justify-content: flex-end;
                  
                  @media (max-width: $breakpoint-mobile) {
                    justify-content: space-between;
                  }
                  
                  .amount {
                    font-weight: 600;
                    color: $amount-color;
                    font-size: 14px;
                    
                    &.patient-amount {
                      color: $patient-amount-color;
                    }
                  }
                }
                
                &.fee-col {
                  @media (max-width: $breakpoint-mobile) {
                    &:before {
                      content: 'Fee: ';
                    }
                  }
                }
                
                &.allowed-col {
                  @media (max-width: $breakpoint-mobile) {
                    &:before {
                      content: 'Allowed: ';
                    }
                  }
                }
                
                &.insurance-col {
                  @media (max-width: $breakpoint-mobile) {
                    &:before {
                      content: 'Insurance: ';
                    }
                  }
                }
                
                &.patient-col {
                  @media (max-width: $breakpoint-mobile) {
                    &:before {
                      content: 'Patient: ';
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    
    .empty-state {
      text-align: center;
      padding: 80px 24px;
      background: $empty-state-bg-color;
      border: 1px solid $empty-state-border-color;
      border-radius: 12px;
      
      @media (max-width: $breakpoint-mobile) {
        padding: 60px 20px;
      }
      
      .empty-state-icon {
        margin-bottom: 24px;
      }
      
      .empty-state-title {
        font-size: 20px;
        font-weight: 600;
        color: $empty-state-title-color;
        margin: 0 0 16px 0;
        
        @media (max-width: $breakpoint-mobile) {
          font-size: 18px;
        }
      }
      
      .empty-state-message {
        font-size: 16px;
        color: $empty-state-text-color;
        line-height: 1.5;
        margin: 0;
        max-width: 500px;
        margin-left: auto;
        margin-right: auto;
        
        @media (max-width: $breakpoint-mobile) {
          font-size: 14px;
        }
      }
    }
  }
}
</style>
